import { useEffect, useRef } from 'react';
import apiClient from '../lib/api/apiClient';
import { useModernToast } from '../components/ModernToast';

interface GlobalNotificationResponse {
  message?: string;
  title?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  link?: string;
}

/**
 * Custom hook that polls for global toast notifications every minute
 * Calls the user/global-toast-notification endpoint and displays a toast if response has body
 */
export const useGlobalNotification = () => {
  const toast = useModernToast();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const pollForNotifications = async () => {
      try {
        const response = await apiClient.get('user/global-toast-notification');
        
        // Check if response has a body with notification data
        if (response.data && (response.data.message || response.data.title)) {
          const notification: GlobalNotificationResponse = response.data;
          
          // Display toast based on notification type
          const type = notification.type || 'info';
          
          switch (type) {
            case 'success':
              toast.success(notification.title || 'Success', notification.message, notification.link);
              break;
            case 'error':
              toast.error(notification.title || 'Error', notification.message, notification.link);
              break;
            case 'warning':
              toast.warning(notification.title || 'Warning', notification.message, notification.link);
              break;
            case 'info':
            default:
              toast.info(notification.title || 'Info', notification.message, notification.link);
              break;
          }
        }
      } catch (error) {
        // Silently handle errors - don't show toast for API errors
        // This prevents spam if the endpoint doesn't exist or returns errors
        console.debug('Global notification polling error:', error);
      }
    };

    // Start polling immediately
    pollForNotifications();

    // Set up interval to poll every minute (60000ms)
    intervalRef.current = setInterval(pollForNotifications, 60000);

    // Cleanup function to clear interval when component unmounts
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [toast]);

  // Return any useful methods if needed in the future
  return {
    // Could add manual trigger method here if needed
  };
};
